import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, TrendingDown, Banknote, FileSpreadsheet, FileText, Sparkles, PoundSterling, Undo2 } from "lucide-react";

const API = api.defaults.baseURL;
const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");
const todayISO = () => new Date().toISOString().slice(0, 10);
const yearStart = () => `${new Date().getFullYear()}-01-01`;

export function FinancialsPanel() {
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(todayISO());
  const [applied, setApplied] = useState({ from: yearStart(), to: todayISO() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAnswer, setAiAnswer] = useState(null);

  const load = useCallback((f, t) => {
    setLoading(true);
    api.get(`/admin/financials?from=${f}&to=${t}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Could not load financials"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(applied.from, applied.to); }, [applied, load]);

  const apply = () => {
    if (from > to) { toast.error("'From' date must be before 'To' date."); return; }
    setApplied({ from, to });
  };

  const askAi = async () => {
    if (!question.trim()) return;
    setAiBusy(true); setAiAnswer(null);
    try {
      const r = await api.post("/admin/financials/ai", { question, from: applied.from, to: applied.to });
      if (r.data.error) toast.error(r.data.error);
      else setAiAnswer(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI helper failed"); }
    setAiBusy(false);
  };

  if (loading || !data) return <div className="text-[#6B7280] text-sm" data-testid="financials-loading">Loading financials…</div>;

  const s = data.summary;
  const yoy = data.yoy;
  const exportUrl = (ext) => `${API}/admin/financials/export.${ext}?from=${applied.from}&to=${applied.to}`;

  return (
    <div data-testid="admin-financials" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="label-eyebrow mb-1">From</div>
            <input data-testid="fin-from" type="date" value={from} onChange={e => setFrom(e.target.value)} className="border border-[#E5E7EB] px-3 py-2 text-sm" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">To</div>
            <input data-testid="fin-to" type="date" value={to} onChange={e => setTo(e.target.value)} className="border border-[#E5E7EB] px-3 py-2 text-sm" />
          </div>
          <button data-testid="fin-apply" onClick={apply} className="de-btn-primary text-xs">Apply</button>
        </div>
        <div className="flex gap-2">
          <a data-testid="fin-export-csv" href={exportUrl("csv")} className="de-btn-secondary text-xs inline-flex items-center gap-2"><FileSpreadsheet size={14} /> CSV</a>
          <a data-testid="fin-export-pdf" href={exportUrl("pdf")} className="de-btn-secondary text-xs inline-flex items-center gap-2"><FileText size={14} /> PDF</a>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="fin-kpis">
        <Kpi testid="fin-cash" icon={Banknote} label="Cash received (net of refunds)" value={gbp(s.cash_received)}
          sub={`Output VAT ${gbp(s.cash_received_vat)} · Net ${gbp(s.cash_received_net)}`} />
        <Kpi testid="fin-contracted" icon={PoundSterling} label="Contracted (won)" value={gbp(s.contracted_value)}
          sub={`${s.contracted_count} job${s.contracted_count === 1 ? "" : "s"} won in period`} />
        <Kpi testid="fin-pipeline" icon={TrendingUp} label="Open pipeline" value={gbp(s.pipeline_value)}
          sub={`${s.pipeline_bookings} unpaid booking${s.pipeline_bookings === 1 ? "" : "s"} · ${s.pipeline_quotes} quote${s.pipeline_quotes === 1 ? "" : "s"}`} />
        <Kpi testid="fin-projection" icon={TrendingUp} label="Projected income" value={gbp(s.projection_value)}
          sub="Outstanding on won jobs" />
        {s.refunds > 0 && (
          <Kpi testid="fin-refunds" icon={Undo2} label="Refunded (in period)" value={gbp(s.refunds)}
            sub="Already deducted from cash received" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div className="border border-[#E5E7EB] bg-white p-5" data-testid="fin-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold uppercase tracking-widest text-[#12263A]">Monthly cash received</h3>
            <span className="text-xs text-[#6B7280]">vs same month last year</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.monthly} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => gbp(v)} contentStyle={{ border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="cash" name="This period" fill="#C79A3B" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cash_prev_year" name="Prev year" fill="#D9C7B6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-[#E5E7EB] bg-[#12263A] text-white p-5 flex flex-col justify-center" data-testid="fin-yoy">
          <div className="text-[11px] uppercase tracking-widest text-[#E8A07E]">Year on year (cash)</div>
          <div className="text-3xl font-black mt-2">{gbp(yoy.this_year)}</div>
          <div className="text-xs text-[#cdbfae] mt-1">this period</div>
          <div className="text-sm mt-3 text-[#cdbfae]">Prior year: {gbp(yoy.last_year)}</div>
          {yoy.pct_change !== null && yoy.pct_change !== undefined ? (
            <div className={`mt-2 inline-flex items-center gap-1 text-sm font-bold ${yoy.pct_change >= 0 ? "text-green-400" : "text-red-400"}`}>
              {yoy.pct_change >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />} {yoy.pct_change > 0 ? "+" : ""}{yoy.pct_change}%
            </div>
          ) : <div className="mt-2 text-xs text-[#cdbfae]">No prior-year data to compare.</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest mb-3">Monthly breakdown</h3>
          <div className="overflow-x-auto border border-[#E5E7EB] bg-white" data-testid="fin-monthly-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F4] text-left"><tr>{["Month", "Cash", "Output VAT", "Net", "Contracted"].map(h => <th key={h} className="px-3 py-2 text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
              <tbody>
                {data.monthly.map(m => (
                  <tr key={m.month} className="border-t border-[#E5E7EB]">
                    <td className="px-3 py-2">{m.label}</td>
                    <td className="px-3 py-2">{gbp(m.cash)}</td>
                    <td className="px-3 py-2 text-[#6B7280]">{gbp(m.vat)}</td>
                    <td className="px-3 py-2">{gbp(m.net)}</td>
                    <td className="px-3 py-2 text-[#6B7280]">{gbp(m.contracted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest mb-3">Projected income by build-start month</h3>
          <div className="overflow-x-auto border border-[#E5E7EB] bg-white" data-testid="fin-projection-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F4] text-left"><tr>{["Start month", "Outstanding", "Jobs"].map(h => <th key={h} className="px-3 py-2 text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
              <tbody>
                {data.projection_by_month.map(p => (
                  <tr key={p.month} className="border-t border-[#E5E7EB]">
                    <td className="px-3 py-2">{p.label}</td>
                    <td className="px-3 py-2 font-semibold">{gbp(p.outstanding)}</td>
                    <td className="px-3 py-2 text-[#6B7280]">{p.jobs}</td>
                  </tr>
                ))}
                {data.projection_by_month.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-[#6B7280]">No outstanding balances on won jobs.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="border border-[#E5E7EB] bg-white p-5" data-testid="fin-ai">
        <div className="label-eyebrow mb-2 flex items-center gap-2"><Sparkles size={14} className="text-[#C79A3B]" /> Ask the finance assistant</div>
        <div className="flex gap-2">
          <input data-testid="fin-ai-input" value={question} onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") askAi(); }}
            placeholder="e.g. How does this period compare to last year? What income is expected next month?"
            className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm" />
          <button data-testid="fin-ai-run" disabled={aiBusy || !question.trim()} onClick={askAi} className="de-btn-primary text-xs whitespace-nowrap">{aiBusy ? "Thinking…" : "Ask"}</button>
        </div>
        {aiAnswer && (
          <div className="mt-3 border border-[#C79A3B] bg-[#F4F4F4] p-4" data-testid="fin-ai-answer">
            <p className="text-sm leading-relaxed">{aiAnswer.answer}</p>
            {Array.isArray(aiAnswer.warnings) && aiAnswer.warnings.length > 0 && (
              <ul className="text-xs text-amber-700 mt-2 list-disc pl-4">{aiAnswer.warnings.map((w, i) => <li key={`${i}-${w}`}>{w}</li>)}</ul>
            )}
          </div>
        )}
        <p className="text-[11px] text-[#6B7280] mt-2">Answers are generated from the figures above for the selected period.</p>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="border border-[#E5E7EB] bg-white p-4">
      <div className="flex items-center gap-2 text-[#6B7280]"><Icon size={15} className="text-[#C79A3B]" /><span className="label-eyebrow">{label}</span></div>
      <div className="text-2xl font-black mt-2 text-[#12263A]">{value}</div>
      <div className="text-[11px] text-[#6B7280] mt-1 leading-snug">{sub}</div>
    </div>
  );
}
